/**
 * DXF HEADER persistence for AutoCAD Dynamic Input system variables.
 *
 * AutoCAD writes each sysvar into the HEADER section as:
 *
 *   9          <- group code for variable name
 *   $DYNMODE   <- variable name with leading $
 *   70         <- group code for the value (integer16 for all DYN* vars)
 *   3          <- numeric value
 *
 * This module provides:
 *  - `readDynVarsFromHeader`  — scan a flat DxfGroup[] (HEADER section body)
 *    and extract every recognized DYN* variable.
 *  - `writeDynVarsToHeaderLines` — produce the DxfGroup[] pairs for a set of
 *    DYN* values, ready to be spliced into or appended to HEADER groups.
 *  - `patchDynVarsIntoHeaderGroups` — in-place update of an existing HEADER
 *    section's DxfGroup[]: replace existing $DYN* entries and append any that
 *    were absent. Preserves all other groups verbatim.
 *
 * AutoCAD reference (R2007+): all nine DYN* variables use group code 70
 * (16-bit integer). TEMPOVERRIDES also uses code 70.
 */

import type { DxfGroup } from "./types";

// ── Public constants ─────────────────────────────────────────────────────────

export const DYN_VAR_HEADER_KEYS = [
  "DYNMODE",
  "DYNPICOORDS",
  "DYNPIFORMAT",
  "DYNPIVIS",
  "DYNDIVIS",
  "DYNDIGRIP",
  "DYNPROMPT",
  "DYNTOOLTIPS",
  "TEMPOVERRIDES",
] as const;

export type DynVarHeaderKey = (typeof DYN_VAR_HEADER_KEYS)[number];

/** Group code used for all DYN* header variables (int16 in AutoCAD DXF spec). */
const DYN_VAR_GROUP_CODE = 70;

/** Map from DXF header key name (without $) to group code. */
const DYN_VAR_CODES: Record<DynVarHeaderKey, number> = {
  DYNMODE: DYN_VAR_GROUP_CODE,
  DYNPICOORDS: DYN_VAR_GROUP_CODE,
  DYNPIFORMAT: DYN_VAR_GROUP_CODE,
  DYNPIVIS: DYN_VAR_GROUP_CODE,
  DYNDIVIS: DYN_VAR_GROUP_CODE,
  DYNDIGRIP: DYN_VAR_GROUP_CODE,
  DYNPROMPT: DYN_VAR_GROUP_CODE,
  DYNTOOLTIPS: DYN_VAR_GROUP_CODE,
  TEMPOVERRIDES: DYN_VAR_GROUP_CODE,
};

const DYN_VAR_KEY_SET = new Set<string>(DYN_VAR_HEADER_KEYS);

function isDynVarKey(name: string): name is DynVarHeaderKey {
  return DYN_VAR_KEY_SET.has(name);
}

// ── Read ─────────────────────────────────────────────────────────────────────

/**
 * Scan the flat group list of a HEADER section and extract all recognized
 * DYN* variables. Groups are expected in the AutoCAD pattern:
 *
 *   { code: 9,  value: "$VARNAME" }
 *   { code: 70, value: "<integer>" }
 *
 * Unknown variables (not in DYN_VAR_HEADER_KEYS) are silently ignored.
 * Groups with a DYN* name but an unexpected code or non-numeric value are also
 * ignored to guard against schema changes in future AutoCAD versions.
 *
 * @param headerGroups The raw DxfGroup[] from the HEADER section body
 *   (excluding the section boundary markers).
 * @returns A partial record containing only the variables found and parsed
 *   successfully.
 */
export function readDynVarsFromHeader(
  headerGroups: DxfGroup[],
): Partial<Record<DynVarHeaderKey, number>> {
  const result: Partial<Record<DynVarHeaderKey, number>> = {};

  for (let i = 0; i < headerGroups.length - 1; i++) {
    const nameGroup = headerGroups[i];
    const valueGroup = headerGroups[i + 1];

    if (!nameGroup || !valueGroup) continue;

    // Variable name groups always have code 9 and value starting with "$"
    if (nameGroup.code !== 9) continue;

    const rawName = nameGroup.value.trim();
    if (!rawName.startsWith("$")) continue;

    const varName = rawName.slice(1); // strip leading $
    if (!isDynVarKey(varName)) continue;

    const expectedCode = DYN_VAR_CODES[varName];
    if (valueGroup.code !== expectedCode) continue;

    const parsed = parseInt(valueGroup.value, 10);
    if (!Number.isFinite(parsed) || Number.isNaN(parsed)) continue;

    result[varName] = parsed;
    i++; // skip the consumed value group
  }

  return result;
}

// ── Write ────────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs that encode the given DYN* values for
 * inclusion in a HEADER section. Each variable produces exactly two groups:
 *
 *   { code: 9,  value: "$VARNAME" }
 *   { code: 70, value: "<integer>" }
 *
 * Only variables explicitly provided in `values` are emitted. Pass the full
 * sysvar snapshot to emit all nine; pass a partial to emit a subset.
 *
 * @param values Record of DYN* variable values to serialize.
 * @returns Flat array of DxfGroup pairs in HEADER variable encoding.
 */
export function writeDynVarsToHeaderLines(
  values: Partial<Record<DynVarHeaderKey, number>>,
): DxfGroup[] {
  const out: DxfGroup[] = [];

  for (const key of DYN_VAR_HEADER_KEYS) {
    const val = values[key];
    if (val === undefined) continue;

    out.push({ code: 9, value: `$${key}` });
    out.push({ code: DYN_VAR_CODES[key], value: String(Math.round(val)) });
  }

  return out;
}

// ── Patch ────────────────────────────────────────────────────────────────────

/**
 * Splice DYN* variable entries into an existing HEADER section group array.
 *
 * Strategy:
 * 1. Walk the existing groups. When a `{ code: 9, value: "$DYN*" }` entry is
 *    found and `values` contains the corresponding key, replace the following
 *    value group with the new value. Mark the key as handled.
 * 2. For any keys in `values` that were NOT found in the existing groups,
 *    append them at the end.
 *
 * This preserves every other HEADER variable verbatim and does not reorder
 * the section, keeping the file as close to the original AutoCAD output as
 * possible.
 *
 * @param existingGroups Mutable copy of the HEADER section groups to patch.
 * @param values The DYN* values to write. Keys absent from `values` are left
 *   untouched in the existing groups.
 * @returns A new DxfGroup[] with the DYN* values patched in.
 */
export function patchDynVarsIntoHeaderGroups(
  existingGroups: DxfGroup[],
  values: Partial<Record<DynVarHeaderKey, number>>,
): DxfGroup[] {
  if (Object.keys(values).length === 0) return existingGroups.slice();

  const handled = new Set<DynVarHeaderKey>();
  const patched: DxfGroup[] = [];

  for (let i = 0; i < existingGroups.length; i++) {
    const group = existingGroups[i]!;

    if (group.code === 9 && group.value.startsWith("$")) {
      const varName = group.value.slice(1);
      if (isDynVarKey(varName) && values[varName] !== undefined) {
        // Replace this name group and its following value group
        patched.push(group); // keep the $NAME group as-is
        const newVal = values[varName]!;
        patched.push({
          code: DYN_VAR_CODES[varName],
          value: String(Math.round(newVal)),
        });
        handled.add(varName);
        i++; // skip the original value group (replaced)
        continue;
      }
    }

    patched.push(group);
  }

  // Append any DYN* vars not found in the original section
  for (const key of DYN_VAR_HEADER_KEYS) {
    if (handled.has(key)) continue;
    const val = values[key];
    if (val === undefined) continue;

    patched.push({ code: 9, value: `$${key}` });
    patched.push({ code: DYN_VAR_CODES[key], value: String(Math.round(val)) });
  }

  return patched;
}
